/**
 * The browser's copy of the permission matrix.
 *
 * Mirrors backend/src/domain/roles.js, and is for *rendering only* — hiding a
 * button the server would refuse anyway. Every one of these capabilities is
 * enforced again in middleware, because anything decided in a browser is
 * decided by whoever owns the browser.
 *
 * Duplicated rather than fetched because it is needed on first paint, before
 * any request completes, and a nav that rearranges itself a second after load
 * is worse than one that is briefly wrong.
 */
export const ROLE = Object.freeze({
  OWNER: 'owner',
  ADMIN: 'admin',
  STAFF: 'staff',
  VIEWER: 'viewer',
});

const CAPABILITY = {
  TAKE_PAYMENT: [ROLE.OWNER, ROLE.ADMIN, ROLE.STAFF],
  REFUND: [ROLE.OWNER, ROLE.ADMIN, ROLE.STAFF],
  CONFIRM_OFFLINE: [ROLE.OWNER, ROLE.ADMIN, ROLE.STAFF],

  VIEW_PAYMENTS: [ROLE.OWNER, ROLE.ADMIN, ROLE.STAFF, ROLE.VIEWER],
  VIEW_CUSTOMERS: [ROLE.OWNER, ROLE.ADMIN, ROLE.STAFF, ROLE.VIEWER],
  VIEW_ACTIVITY: [ROLE.OWNER, ROLE.ADMIN],
  MANAGE_CUSTOMERS: [ROLE.OWNER, ROLE.ADMIN, ROLE.STAFF],

  MANAGE_PAYMENT_LINKS: [ROLE.OWNER, ROLE.ADMIN, ROLE.STAFF],
  MANAGE_METHODS: [ROLE.OWNER, ROLE.ADMIN],
  MANAGE_PROFILE: [ROLE.OWNER, ROLE.ADMIN],

  MANAGE_PROVIDERS: [ROLE.OWNER, ROLE.ADMIN],
  MANAGE_API_KEYS: [ROLE.OWNER, ROLE.ADMIN],
  MANAGE_WEBHOOKS: [ROLE.OWNER, ROLE.ADMIN],

  MANAGE_TEAM: [ROLE.OWNER, ROLE.ADMIN],
  MANAGE_OWNERS: [ROLE.OWNER],
};

export const can = (role, capability) => (CAPABILITY[capability] ?? []).includes(role);

export const ROLE_LABEL = {
  [ROLE.OWNER]: 'Owner',
  [ROLE.ADMIN]: 'Admin',
  [ROLE.STAFF]: 'Staff',
  [ROLE.VIEWER]: 'Viewer',
};

export default { ROLE, can, ROLE_LABEL };
